import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowClockwise, Circle, MouseSimple, ChartLine, Users, Clock } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function Avatar({ user, size = 32 }) {
  const src = resolveImg(user?.avatar_url);
  const letter = (user?.username || "?").slice(0, 1).toUpperCase();
  if (src) {
    return <img src={src} alt={user?.username} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  }
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black shrink-0 text-xs">
      {letter}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleString();
}

function StatCard({ label, value, Icon, color, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="flex items-center gap-3">
        <div className={`h-11 w-11 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center ${color}`}>
          <Icon size={22} weight="bold" />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-3xl font-black text-slate-900 dark:text-white leading-tight" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminActivity() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // all | online | surfing | today

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/activity");
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // live refresh every 10s
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <AdminLayout><div className="text-slate-500">Loading activity…</div></AdminLayout>;

  const rows = (data.users || []).filter((u) => {
    if (filter === "online") return u.is_online;
    if (filter === "surfing") return u.is_surfing_now;
    if (filter === "today") return u.surfs_today > 0;
    if (filter === "yesterday") return u.surfs_yesterday > 0;
    return true;
  });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Live activity</div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Who&apos;s active right now
          </h1>
          <div className="text-xs text-slate-500 mt-1">
            Auto-refreshing every 10 seconds · last update {relTime(data.generated_at)}
          </div>
        </div>
        <button
          data-testid="activity-refresh-btn"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold px-4 py-2 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200"
        >
          <ArrowClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Online now" value={data.counts.online_now} Icon={Circle} color="text-green-500" testid="stat-online-now" />
        <StatCard label="Surfing now" value={data.counts.surfing_now} Icon={MouseSimple} color="text-blue-600" testid="stat-surfing-now" />
        <StatCard label="Surfers last 24h" value={data.counts.surfers_last_24h} Icon={Users} color="text-purple-600" testid="stat-surfers-24h" />
        <StatCard label="Surfers yesterday" value={data.counts.surfers_yesterday ?? 0} Icon={Clock} color="text-amber-500" testid="stat-surfers-yesterday" />
        <StatCard label="Visible users" value={data.counts.total_visible} Icon={ChartLine} color="text-orange-500" testid="stat-total-visible" />
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[
          { k: "all", label: "All active" },
          { k: "surfing", label: "Surfing now" },
          { k: "online", label: "Online now" },
          { k: "today", label: "Surfed today" },
          { k: "yesterday", label: "Surfed yesterday" },
        ].map((f) => {
          const active = filter === f.k;
          const cls = active ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700";
          return (
            <button
              key={f.k}
              data-testid={`activity-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${cls}`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] uppercase font-bold tracking-widest text-slate-500">
              <tr>
                <th className="p-3 text-left">User</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Plan</th>
                <th className="p-3 text-right">Today</th>
                <th className="p-3 text-right" title="Sites surfed 24-48h ago">Yesterday</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-left">Last surf</th>
                <th className="p-3 text-left">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="activity-table">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-slate-500">
                    No users match this filter.
                  </td>
                </tr>
              )}
              {rows.map((u) => {
                let statusLabel, statusCls;
                if (u.is_surfing_now) { statusLabel = "surfing now"; statusCls = "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300"; }
                else if (u.is_online) { statusLabel = "online"; statusCls = "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"; }
                else if (u.surfed_last_24h) { statusLabel = "recent"; statusCls = "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300"; }
                else { statusLabel = "idle"; statusCls = "bg-slate-100 text-slate-500"; }
                return (
                  <tr key={u.id} data-testid={`activity-row-${u.username}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="p-3">
                      <Link to={`/u/${u.id}`} className="flex items-center gap-2 hover:underline">
                        <Avatar user={u} size={32} />
                        <div>
                          <div className="font-black text-slate-900 dark:text-white inline-flex items-center gap-1">@{u.username}<UserBadge user={u} size={12} /></div>
                          <div className="text-[11px] text-slate-500">{u.email}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="p-3">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${statusCls}`}>
                        {u.is_surfing_now && <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />}
                        {u.is_online && !u.is_surfing_now && <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />}
                        {statusLabel}
                      </span>
                    </td>
                    <td className="p-3 text-slate-700 dark:text-slate-200">{u.membership || "Free"}</td>
                    <td className="p-3 text-right font-black text-slate-900 dark:text-white" data-testid={`activity-today-${u.username}`}>{u.surfs_today}</td>
                    <td className="p-3 text-right font-black text-slate-900 dark:text-white" data-testid={`activity-yesterday-${u.username}`}>{u.surfs_yesterday ?? 0}</td>
                    <td className="p-3 text-right font-black text-slate-900 dark:text-white">{u.surfs_total}</td>
                    <td className="p-3 text-xs text-slate-500">{relTime(u.last_surf_at || u.last_surf_yesterday_at)}</td>
                    <td className="p-3 text-xs text-slate-500">{relTime(u.last_login)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
